import {
  Injectable,
  Signal,
  WritableSignal,
  computed,
  signal,
} from '@angular/core';

export type ToastVariant = 'success' | 'info' | 'warning' | 'error';

export interface ToastOptions {
  /** Auto-dismiss after this many ms. 0 => sticky (user must close). */
  durationMs?: number;
  /** i18n params for ngx-translate interpolation. */
  params?: Record<string, unknown>;
}

export interface Toast {
  id: string;
  variant: ToastVariant;
  messageKey: string;
  options: ToastOptions;
  createdAt: number;
  leaving: boolean;
}

const LEAVE_DURATION_MS = 150;

const DEFAULT_DURATION_MS: Record<ToastVariant, number> = {
  success: 6000,
  info: 6000,
  warning: 8000,
  error: 0,
};

function nextId(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

@Injectable({ providedIn: 'root' })
export class NotificationService {
  public readonly toasts: Signal<Toast[]>;

  private readonly _toasts: WritableSignal<Toast[]> = signal([]);
  private readonly timers = new Map<string, ReturnType<typeof setTimeout>>();

  constructor() {
    this.toasts = computed(() => this._toasts());
  }

  public success(messageKey: string, options: ToastOptions = {}): string {
    return this.push('success', messageKey, options);
  }

  public info(messageKey: string, options: ToastOptions = {}): string {
    return this.push('info', messageKey, options);
  }

  public warn(messageKey: string, options: ToastOptions = {}): string {
    return this.push('warning', messageKey, options);
  }

  public error(messageKey: string, options: ToastOptions = {}): string {
    return this.push('error', messageKey, options);
  }

  public dismiss(id: string): void {
    this.clearTimer(id);
    this._toasts.update((list) =>
      list.map((t) => (t.id === id ? { ...t, leaving: true } : t)),
    );
    setTimeout(() => this.remove(id), LEAVE_DURATION_MS);
  }

  public clear(): void {
    for (const id of this.timers.keys()) {
      this.clearTimer(id);
    }
    this._toasts.set([]);
  }

  private push(
    variant: ToastVariant,
    messageKey: string,
    options: ToastOptions,
  ): string {
    const toast: Toast = {
      id: nextId(),
      variant,
      messageKey,
      options,
      createdAt: Date.now(),
      leaving: false,
    };

    this._toasts.update((list) => [...list, toast]);

    const duration =
      options.durationMs !== undefined
        ? options.durationMs
        : DEFAULT_DURATION_MS[variant];

    if (duration > 0) {
      const handle = setTimeout(() => this.dismiss(toast.id), duration);

      this.timers.set(toast.id, handle);
    }
    return toast.id;
  }

  private remove(id: string): void {
    this._toasts.update((list) => list.filter((t) => t.id !== id));
  }

  private clearTimer(id: string): void {
    const handle = this.timers.get(id);

    if (handle !== undefined) {
      clearTimeout(handle);
      this.timers.delete(id);
    }
  }
}
